/**
 * QUIPWA — IdbBlobStore
 *
 * Tiny key → ArrayBuffer store on IndexedDB, used to persist serialized SQLite
 * databases. One IndexedDB database (`QUIPWA-sqljs`) with one object store
 * (`databases`); keys are `QUIPWA:<name>`, unchanged from earlier versions so
 * existing user data is picked up as-is.
 *
 * The connection is opened once per IDBFactory and reused (it used to be
 * reopened on every single load and save).
 *
 * Writes must survive the page going away right after they start (a reload
 * just after saving). Measured in Chromium 148 and Firefox 150 (see
 * docs/modulos/almacenamiento.md): a put issued from `pagehide` is lost, even
 * 1 KB of it, unless the transaction is committed explicitly — auto-commit
 * waits for the request's success event, which a page being unloaded never
 * gets. With `commit()` the browser finishes the write on its own. So `put`
 * issues the request before it returns (when the connection is open) and
 * commits at once.
 */

const IDB_DB_NAME = 'QUIPWA-sqljs'
const IDB_STORE = 'databases'

const connections = new WeakMap<IDBFactory, Promise<IDBDatabase>>()
/** The same connections once open, so a write can start without awaiting. */
const ready = new WeakMap<IDBFactory, IDBDatabase>()

function open(factory: IDBFactory): Promise<IDBDatabase> {
  let pending = connections.get(factory)
  if (!pending) {
    pending = new Promise<IDBDatabase>((resolve, reject) => {
      const req = factory.open(IDB_DB_NAME, 1)
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(IDB_STORE)) req.result.createObjectStore(IDB_STORE)
      }
      req.onsuccess = () => {
        const db = req.result
        // Another tab upgrading the schema: let it, and reopen lazily next time.
        db.onversionchange = () => {
          db.close()
          connections.delete(factory)
          ready.delete(factory)
        }
        ready.set(factory, db)
        resolve(db)
      }
      req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'))
      req.onblocked = () => reject(new Error('IndexedDB open blocked by another tab'))
    })
    pending.catch(() => connections.delete(factory))
    connections.set(factory, pending)
  }
  return pending
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'))
  })
}

/** Issue the put and commit it right away; resolves once the write is durable. */
function write(db: IDBDatabase, key: string, value: ArrayBuffer): Promise<void> {
  let tx: IDBTransaction
  try {
    tx = db.transaction(IDB_STORE, 'readwrite')
    tx.objectStore(IDB_STORE).put(value, key)
    // Safari < 15 has no commit(): there the write auto-commits, but not across an unload.
    if (typeof tx.commit === 'function') tx.commit()
  } catch (err) {
    return Promise.reject(err)
  }
  // Resolve on commit, not on request success: only then is it durable.
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB write failed'))
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB write aborted'))
  })
}

export class IdbBlobStore {
  private readonly factory: () => IDBFactory

  /** @param factory resolved lazily so tests can swap `globalThis.indexedDB` */
  constructor(factory: () => IDBFactory = () => indexedDB) {
    this.factory = factory
  }

  async get(key: string): Promise<ArrayBuffer | null> {
    const db = await open(this.factory())
    const value = await promisify(db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(key))
    return (value as ArrayBuffer | undefined) ?? null
  }

  /**
   * Store `value` under `key`; resolves once it is durable. With the
   * connection already open, the write is in IndexedDB's hands before this
   * returns — what a `pagehide` handler needs.
   */
  put(key: string, value: ArrayBuffer): Promise<void> {
    const factory = this.factory()
    const db = ready.get(factory)
    if (db) return write(db, key, value)
    return open(factory).then((opened) => write(opened, key, value))
  }

  async delete(key: string): Promise<void> {
    const db = await open(this.factory())
    await promisify(db.transaction(IDB_STORE, 'readwrite').objectStore(IDB_STORE).delete(key))
  }

  async keys(): Promise<string[]> {
    const db = await open(this.factory())
    const keys = await promisify(db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).getAllKeys())
    return keys.map(String)
  }
}

export const defaultBlobStore = new IdbBlobStore()
